// パリィの音 (2026-10-09)。配布版の既定 = 普段の弾き「a 柳の型」・致命を弾いた時「B 押し返しの二段」(ユーザーが聞き比べて選んだ)。
//   ユーザー原文「ボスの致命を弾いた時の音はゼンゼロのイーシェンのパーフェクトガードみたいな音で、
//   普段のパリィの音は柳のパーフェクトガードみたいな音かゼンゼロのパリィ音みたいな音がいい」。
//   設計・手本の確かめ・聞き比べ方 = docs/research/2026-10-09-parry-sound-candidates.md
//
//   開発版の比較: ?parrySound=now|a|b|c / ?execParrySound=now|a|b|c。now = 旧の音 (冷たい銀・audio.ts playParryCold／
//   致命を弾いた時は旧の弾き＋ガードの音)。指定なし = 配布版と同じ既定 (a / b)。
//   URL を読むのは game.ts の import.meta.env.DEV の箱の中だけ。
//
//   採用の2本は本番の置き場 public/sfx/parry_yanagi.wav・parry_exec_push.wav (git 管理・48kHz 1ch 16bit = 今の鋼と同じ形式)。
//   作り方 = scripts/render_parry_sound_candidates.py --adopt (ユーザーが聞いた候補の WAV から作る)。
//   残りの候補 (b・c・A・C) の WAV は開発版の比較用で git 外 (public/sfx/candidates/・同じスクリプトで書き出す)。
//   読めていない時 (読み込み前・候補を置いていない) は旧の音が鳴る (弾いたのに無音、にはならない)。
//   ボスの致命を弾く場面 (src/exec_parry.ts) は 2026-10-10 から配布版の既定 = B は配布版でも鳴る (旧の弾けない版は DEV ?execParry=off)。
//   どの候補も SfxId 'parry' として鳴る = プレイヤーの音の優先 (src/sound_priority.ts) がそのまま効く。

export type ParrySoundVariant = 'now' | 'a' | 'b' | 'c'
export type ParrySoundCandidateKey = Exclude<ParrySoundVariant, 'now'>

export type ParrySoundCandidate = {
  /** audio.ts の sfxSamples のキー */
  sample: string
  /** 読む場所。採用 = /sfx/<名前>.wav (本番) / 比較用 = /sfx/candidates/<名前>.wav (git 外) */
  url: string
  /** 配布版で使う (本番の置き場にある) */
  adopted: boolean
  label: string
  /** 手本のどこを借りたか (1行) */
  borrowed: string
  /** WAV に掛ける大きさ (dB)。実際に鳴った音 (limiter の後) を録って、今の弾きと聞こえの大きさを合わせた値。
   *  測り方 = scripts/record_parry_sound_candidates.mjs (研究文書の「大きさ」の節)。 */
  gainDb: number
  /** 石の広間の響きへ送る量 (WAV にも響きを薄く焼いてあるので、今の弾き 0.4 前後より少なめ)。 */
  reverbSend: number
}

export const PARRY_SOUND_CANDIDATES: Record<ParrySoundCandidateKey, ParrySoundCandidate> = {
  a: {
    sample: 'parry_yanagi', url: '/sfx/parry_yanagi.wav?v=1', adopted: true, label: '柳の型 (刀の澄んだ一打)',
    borrowed: '月城柳の受け (刀・素早い返し) の「細く鋭い一打＋短い高い鳴り＋火花」。音色は推測で作った',
    gainDb: -8.0, reverbSend: 0.12,
  },
  b: {
    sample: 'parry_n_b', url: '/sfx/candidates/parry_n_b.wav?v=1', adopted: false, label: 'ゼンゼロのパリィの型 (低いドン＋長いキーン)',
    borrowed: '手元の ZZZ 特殊ガードの音を測った「42〜73Hz のドン (約0.3秒で引く)」と「2394/2689/3766/5669Hz の澄んだ鳴り」',
    gainDb: -7.9, reverbSend: 0.12,
  },
  c: {
    sample: 'parry_n_c', url: '/sfx/candidates/parry_n_c.wav?v=1', adopted: false, label: '今の音＋ゼンゼロの鳴り',
    borrowed: '今の冷たい銀 (同じ鋼2本・同じ衝撃) に、ZZZ の低いドンと 2780/3209/4540Hz の鳴りを足した',
    gainDb: -8.1, reverbSend: 0.12,
  },
}

export const EXEC_PARRY_SOUND_CANDIDATES: Record<ParrySoundCandidateKey, ParrySoundCandidate> = {
  a: {
    sample: 'parry_x_a', url: '/sfx/candidates/parry_x_a.wav?v=1', adopted: false, label: '儀玄の型A 鐘で受ける',
    borrowed: '儀玄 (玄墨・道士) のパーフェクトブロックの「重い受け止め」を、深いドン＋銅の鐘の長い鳴り＋墨の広がる風で',
    gainDb: -4.9, reverbSend: 0.18,
  },
  b: {
    sample: 'parry_exec_push', url: '/sfx/parry_exec_push.wav?v=1', adopted: true, label: '儀玄の型B 押し返しの二段',
    borrowed: '儀玄の受け止めに、SEKIRO の重い攻撃の弾きの「ガキン→ゴン」の二段と押されて擦れる音を合わせた',
    gainDb: -5.6, reverbSend: 0.18,
  },
  c: {
    sample: 'parry_x_c', url: '/sfx/candidates/parry_x_c.wav?v=1', adopted: false, label: '儀玄の型C 金の光が散る',
    borrowed: 'ZZZ 特殊ガードの「画面が金に染まる」瞬間を、咲くように重なる高い鳴り (4038〜6052Hz・測った音程) ＋低い鐘で',
    gainDb: -5.6, reverbSend: 0.18,
  },
}

/** ゲームの実際の出力 (limiter の後) を録って測った大きさ (400ms の最大ラウドネス・LUFS 近似・2026-10-09)。
 *  録り方 = scripts/record_parry_sound_candidates.mjs → scripts/sfx_measure.py。候補は下の atGainDb の大きさで録った値。
 *  tests/parry_sound.test.ts が「弾きが一番大きい」「候補が今の弾きと同じくらい」をこの表で固定する。
 *  採用の2本 (parry_yanagi・parry_exec_push) は本番の置き場の 1ch の WAV で録り直して同じ値 (±0.1dB) を確かめた。
 *  gainDb を変えたら録り直してここも直す (テストは gainDb の差を足して見積もるので、直し忘れは見積もりで落ちる)。 */
export const MEASURED_OUTPUT_M400 = {
  now: { just: -18.4, normal: -19.6, exec: -18.7 },
  candidates: {
    parry_yanagi: { just: -18.7, normal: -19.4, atGainDb: -8.0 },
    parry_n_b: { just: -18.9, normal: -19.7, atGainDb: -7.9 },
    parry_n_c: { just: -19.0, normal: -19.6, atGainDb: -8.1 },
    parry_x_a: { just: -16.7, normal: -16.7, atGainDb: -4.9 },
    parry_exec_push: { just: -16.5, normal: -16.5, atGainDb: -5.6 },
    parry_x_c: { just: -16.6, normal: -16.6, atGainDb: -5.6 },
  } as Record<string, { just: number; normal: number; atGainDb: number }>,
  /** プレイヤーの他の音 (今の音)。振り・3段目の振り・命中・3段目の命中・ガード・被弾。 */
  others: { attack_swing: -36.1, attack_swing_heavy: -31.2, hit_boss: -24.2, hit_boss_heavy: -25.1, guard_player: -22.6, hit_player: -21.4 },
} as const

const ALL = [...Object.values(PARRY_SOUND_CANDIDATES), ...Object.values(EXEC_PARRY_SOUND_CANDIDATES)]
/** 本番で先読みする採用の2本 [キー, URL] (audio.ts preloadSfxSamples)。 */
export const ADOPTED_PARRY_SOUND_FILES: readonly [string, string][] = ALL.filter((c) => c.adopted).map((c) => [c.sample, c.url])
/** 開発版の比較用の候補 [キー, URL] (audio.ts preloadParrySoundCandidates・git 外)。 */
export const PARRY_SOUND_CANDIDATE_FILES: readonly [string, string][] = ALL.filter((c) => !c.adopted).map((c) => [c.sample, c.url])
export const PARRY_SOUND_SAMPLES: readonly string[] = ALL.map((c) => c.sample)

/** 配布版の既定 (2026-10-09 ユーザー採用)。 */
export const PARRY_SOUND_DEFAULT: ParrySoundCandidateKey = 'a'
export const EXEC_PARRY_SOUND_DEFAULT: ParrySoundCandidateKey = 'b'

/** URL の値 → 版。now|a|b|c 以外 (指定なし含む) は fallback (= 配布版の既定)。 */
export function parseParrySoundVariant(raw: string | null | undefined, fallback: ParrySoundVariant = 'now'): ParrySoundVariant {
  return raw === 'now' || raw === 'a' || raw === 'b' || raw === 'c' ? raw : fallback
}

let normalVariant: ParrySoundVariant = PARRY_SOUND_DEFAULT
let execVariant: ParrySoundVariant = EXEC_PARRY_SOUND_DEFAULT
export function setParrySoundVariant(raw: string | null | undefined): ParrySoundVariant {
  normalVariant = parseParrySoundVariant(raw, PARRY_SOUND_DEFAULT); return normalVariant
}
export function setExecParrySoundVariant(raw: string | null | undefined): ParrySoundVariant {
  execVariant = parseParrySoundVariant(raw, EXEC_PARRY_SOUND_DEFAULT); return execVariant
}
export function parrySoundVariant(): ParrySoundVariant { return normalVariant }
export function execParrySoundVariant(): ParrySoundVariant { return execVariant }
/** 比較用の候補 (git 外) を読む必要があるか = 既定以外の候補を選んでいる。 */
export function parrySoundCandidatesWanted(): boolean {
  const extra = (v: ParrySoundVariant, d: ParrySoundCandidateKey) => v !== 'now' && v !== d
  return extra(normalVariant, PARRY_SOUND_DEFAULT) || extra(execVariant, EXEC_PARRY_SOUND_DEFAULT)
}

/** 普段のジャストでない弾き = 少し低く・少し小さく (今の音もジャストと普通で高さを変えている・ジャストは基準のまま)。 */
export const PARRY_NORMAL_RATE = 0.95
export const PARRY_NORMAL_GAIN_DB = 0

export type ParrySoundPlan =
  | { kind: 'now' }
  | { kind: 'sample'; sample: string; gain: number; rate: number; reverbSend: number; dropGuard: boolean }

/** 弾いた瞬間に何を鳴らすか。exec = ボスの致命を弾いた (hits.ts の parry_spark.exec)。 */
export function planParrySound(
  hit: { exec: boolean; intensity: number; isJust: boolean },
  normal: ParrySoundVariant = normalVariant,
  exec: ParrySoundVariant = execVariant,
): ParrySoundPlan {
  if (hit.exec && exec !== 'now') {
    const c = EXEC_PARRY_SOUND_CANDIDATES[exec]
    // 致命を弾いた時の候補は1本で完結させる (今は弾き＋ガードを重ねている。ガードの音は重ねない)。
    return { kind: 'sample', sample: c.sample, gain: 10 ** (c.gainDb / 20), rate: 1, reverbSend: c.reverbSend, dropGuard: true }
  }
  if (normal === 'now') return { kind: 'now' }
  const c = PARRY_SOUND_CANDIDATES[normal]
  // 強さ (連続で弾いた段・技の重さ 0.25〜1) で ±2dB ほどだけ動かす。致命を弾いた時は今と同じく強さ最大扱い。
  const w = hit.exec ? 1 : Math.max(0.25, Math.min(1, hit.intensity))
  const db = c.gainDb + (hit.isJust ? 0 : PARRY_NORMAL_GAIN_DB) + 20 * Math.log10(0.8 + 0.2 * w)
  return { kind: 'sample', sample: c.sample, gain: 10 ** (db / 20), rate: hit.isJust ? 1 : PARRY_NORMAL_RATE,
    reverbSend: c.reverbSend, dropGuard: false }
}
